
import { Component, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CreateUserComponent } from './create-user/create-user.component';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { LeaderService } from '../../services/leader.service';

export interface UserData {
  displayName: string;
  roleName: string;
  email: string;
}

export interface EligibleUserData {
  id: string;
  displayName: string;
  roleName?: string;
  email: string;
}

export interface DropdownOption {
  label: string;
  value: EligibleUserData;
}

export interface UserRoleOption {
  label: string;
  value: number;
}

interface LtkUserRole {
  id: number;
  name: string;
}

export interface HospitalData {
  hospitalName: string;
  address: string;
  manager: string;
  managerEmail: string;
  regionalManager: string;
  director: string;
  vicePresident: string;
}

export interface PracticeData {
  practiceSKey: number;
  practiceName: string;
  isCritical: boolean;
}

@Component({
  selector: 'vc-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss'],
  standalone: false
})
export class UserManagementComponent implements OnInit, AfterViewInit {
  private userPaginator!: MatPaginator;
  private userSort!: MatSort;
  private practicePaginator!: MatPaginator;
  private practiceSort!: MatSort;

  finalCriticalList: any[] = [];

  eligibleUsers: EligibleUserData[] = [];
  eligibleUserOptions: DropdownOption[] = [];
  showAssignUserDialog = false;
  selectedUserForAssignment: UserData | null = null;
  selectedEligibleUser: EligibleUserData | null = null;
  isAssigningUser = false;
  showUnassignUserDialog = false;
  selectedUserForUnassign: any = null;
  isUnassigningUser = false;

  showPrimeDialog = false;
   isLoading: boolean = true;

  userDisplayedColumns: string[] = ['displayName', 'roleName', 'email','actions'];
  userDataSource = new MatTableDataSource<UserData>([]);
  practiceDisplayedColumns: string[] = ['select', 'practiceName', 'isCritical'];
  practiceDataSource = new MatTableDataSource<PracticeData>([]);
  selectedPracticeKeys: Set<number> = new Set();
  selectedPractices: any[] = [];
  userRole: any[] = [];
  userRoleOptions: UserRoleOption[] = [];
  selectedUserRole: number | null = null;
  assignDialogSubmitted = false;
  eligibleUserList: any;
  eligibleUserSearchTimeout: any;
  eligibleUserSearchRequestId = 0;

  isAssignHospitalMode = false;
  hospitalOptions: any[] = [];
  selectedHospital: any = null;
  userOverrides: Map<number, any[]> = new Map();

  showUnassignHospitalDialog = false;
  selectedHospitalForUnassign: any = null;
  isUnassigningHospital = false;
  isLoadingHospitals = false;

  get isCriticalButtonEnabled(): boolean {
    return this.selectedPracticeKeys && this.selectedPracticeKeys.size > 0;
  }

  @ViewChild('userPaginator') set matUserPaginator(mp: MatPaginator) {
    this.userPaginator = mp;
    this.setUserDataSourceAttributes();
  }

  @ViewChild('userSort') set matUserSort(ms: MatSort) {
    this.userSort = ms;
    this.setUserDataSourceAttributes();
  }

  @ViewChild('practicePaginator') set matPracticePaginator(mp: MatPaginator) {
    this.practicePaginator = mp;
    this.setPracticeDataSourceAttributes();
  }

  @ViewChild('practiceSort') set matPracticeSort(ms: MatSort) {
    this.practiceSort = ms;
    this.setPracticeDataSourceAttributes();
  }

  constructor(private dialog: MatDialog, private leaderService: LeaderService) {}

  ngOnInit(): void {
    this.practiceDataSource.filterPredicate = (data: PracticeData, filter: string): boolean => {
      const normalizedFilter = filter.trim().toLowerCase();
      const searchableText = [
        data.practiceName,
        data.practiceSKey,
        data.isCritical ? 'yes' : 'no'
      ]
        .join(' ')
        .toLowerCase();

      return searchableText.includes(normalizedFilter);
    };

    this.creticalHospitalList();
    this.finalCriticalList = [];
    this.userList();
    this.loadHospitals();
  }

  loadHospitals(): void {
    this.leaderService.getHospitalList().subscribe({
      next: (res: any) => {
        const hospitals = res?.data || [];
        this.hospitalOptions = hospitals.map((h: any) => ({
          label: h.practiceName,
          value: h
        }));
      }
    });
  }

  ngAfterViewInit(): void {
    this.setUserDataSourceAttributes();
    this.setPracticeDataSourceAttributes();
  }

  private setUserDataSourceAttributes(): void {
    if (this.userDataSource && this.userPaginator && this.userSort) {
      this.userDataSource.paginator = this.userPaginator;
      this.userDataSource.sort = this.userSort;
    }
  }

  private setPracticeDataSourceAttributes(): void {
    if (this.practiceDataSource && this.practicePaginator && this.practiceSort) {
      this.practiceDataSource.paginator = this.practicePaginator;
      this.practiceDataSource.sort = this.practiceSort;
    }
  }

  userrole(): void {
    this.leaderService.ltkuserrole().subscribe({
      next: (res: any) => {
        console.log('User Roles:', res);
        const rolesFromApi = Array.isArray(res?.data) ? res.data : [];
        const normalizedRoles: LtkUserRole[] = rolesFromApi;

        this.userRole = normalizedRoles.length ? normalizedRoles : [];
        this.userRoleOptions = normalizedRoles.map((role: LtkUserRole) => ({
          label: role.name === 'LTKRM' ? 'RM' : role.name,
          value: role.id
        }));
      },
      error: (err) => {
        console.error('Failed to fetch user roles', err);
        this.userRoleOptions = [];
      }
    });
  }

  userList(): void {
    this.isLoading = true;
    this.userDisplayedColumns = ['displayName', 'roleName', 'email', 'hospitalStatus', 'actions'];
    this.leaderService.ltkUserList().subscribe({
      next: (res: any) => {
        const users = res;
        console.log('User List:', users);
        // Transform LTKRM to RM for display
        const transformedUsers = users.map((user: any) => ({
          ...user,
          roleName: user.roleName === 'LTKRM' ? 'RM' : user.roleName
        }));
        this.userDataSource.data = transformedUsers;
        this.setUserDataSourceAttributes();
        this.userDataSource.paginator?.firstPage();
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }



  hasHospitalOverride(userId: number): boolean {
    const overrides = this.userOverrides.get(userId);
    return !!overrides && overrides.some(o => o.isOverride === true);
  }

  getHospitalStatus(userId: number): string {
    const overrides = this.userOverrides.get(userId);
    if (!overrides || overrides.length === 0) return 'None';
    const assigned = overrides.filter(o => o.isOverride === true);
    if (assigned.length === 0) return 'None';
    return assigned.map(a => a.practiceName).join(', ');
  }

  onEligibleUserFilter(event: any): void {
    const searchText = (event?.filter || '').toString().trim();

    if (this.eligibleUserSearchTimeout) {
      clearTimeout(this.eligibleUserSearchTimeout);
    }

    if (!searchText || searchText.length < 2) {
      this.eligibleUserSearchRequestId++;
      this.eligibleUsers = [];
      this.eligibleUserOptions = [];
      return;
    }

    this.eligibleUserSearchTimeout = setTimeout(() => {
      this.eligibleUserListBySearch(searchText);
    }, 300);
  }

  private eligibleUserListBySearch(searchText: string): void {
    const requestId = ++this.eligibleUserSearchRequestId;
    const normalizedSearch = searchText.toLowerCase();

    this.leaderService.searchUserDataList(searchText).subscribe({
      next: (res: any) => {
        if (requestId !== this.eligibleUserSearchRequestId) {
          return;
        }

        const allUserSuggestions = Array.isArray(res?.data)
          ? res.data
          : (Array.isArray(res) ? res : []);

        const sourceUsers = allUserSuggestions
          .filter((user: any) => (user?.email || user?.Email || '').toLowerCase())
          .filter((user: any) => {
            const displayName = (user?.displayName || user?.DisplayName || user?.userName || user?.UserName || '').toLowerCase();
            const email = (user?.email || user?.Email || '').toLowerCase();
            return displayName.includes(normalizedSearch) || email.includes(normalizedSearch);
          });

        this.eligibleUsers = sourceUsers.map((user: any) => this.mapEligibleUserData(user));
        this.eligibleUserOptions = this.eligibleUsers.map((user) => ({
          label: `${user.displayName}${user.email ? ` (${user.email})` : ''}`,
          value: user
        }));

        const hasCurrentSelection = !!this.selectedEligibleUser;
        const hasSelectionInResult = hasCurrentSelection && this.eligibleUsers.some(
          (user) => user.id === this.selectedEligibleUser?.id
        );

        if (hasCurrentSelection && !hasSelectionInResult) {
          this.selectedEligibleUser = null;
        }
      },
      error: () => {
        if (requestId !== this.eligibleUserSearchRequestId) {
          return;
        }

        this.eligibleUsers = [];
        this.eligibleUserOptions = [];
      }
    });
  }



  private mapEligibleUserData(user: any): EligibleUserData {
    const roleName = user?.roleName || user?.RoleName || user?.userRole || user?.UserRole || '';
    return {
      displayName: user?.displayName || user?.DisplayName || user?.userName || user?.UserName || '',
      roleName: roleName === 'LTKRM' ? 'RM' : roleName,
      id: user?.id || user?.uid || '',email: user?.email || user?.Email || ''
    };
  }

  cretiaclHospitalUpdate(): void {
    this.leaderService.creticalHospitalUpdate(this.finalCriticalList).subscribe({
      next: () => {}
    });
  }

  creticalHospitalList(): void {
    this.leaderService.getCriticalHospitals().subscribe({
      next: (res: any) => {
        const practices = res?.data || [];
        this.practiceDataSource.data = practices;
      }
    });
  }

  onPrimeDialogCancel(): void {
    this.showPrimeDialog = false;
    this.finalCriticalList = [];
    this.selectedPractices = [];
    this.selectedPracticeKeys.clear();
  }

  onPrimeDialogConfirm(): void {
    this.showPrimeDialog = false;
    this.cretiaclHospitalUpdate();
    this.finalCriticalList = [];
    this.selectedPractices = [];
    this.selectedPracticeKeys.clear();
  }

  isPracticeSelected(row: any): boolean {
    return this.selectedPracticeKeys && this.selectedPracticeKeys.has(row.practiceSKey);
  }

  onPracticeCheckboxChange(row: any, checked: boolean): void {
    if (checked) {
      if (!this.selectedPractices.some((practice) => practice.practiceSKey === row.practiceSKey)) {
        this.selectedPractices.push(row);
      }
    } else {
      this.selectedPractices = this.selectedPractices.filter((practice) => practice.practiceSKey !== row.practiceSKey);
    }

    if (checked) {
      this.selectedPracticeKeys.add(row.practiceSKey);
    } else {
      this.selectedPracticeKeys.delete(row.practiceSKey);
    }

    this.finalCriticalList = this.selectedPractices.map((practice) => ({
      PracticeSKey: practice.practiceSKey,
      isCritical: true
    }));
  }

  togglePracticeCritical(row: PracticeData): void {
    const nextValue = !row.isCritical;
    this.updatePracticeCritical(row, nextValue);
  }

  onPracticeCriticalChange(row: PracticeData, checked: boolean): void {
    this.updatePracticeCritical(row, checked);
  }

  private updatePracticeCritical(row: PracticeData, isCritical: boolean): void {
    const payload = [{
      PracticeSKey: row.practiceSKey,
      isCritical
    }];

    this.leaderService.creticalHospitalUpdate(payload).subscribe({
      next: () => {
        row.isCritical = isCritical;
        this.practiceDataSource.data = [...this.practiceDataSource.data];
        this.leaderService.showNotification('Critical status updated successfully', false);
      },
      error: (err) => {
        console.error('Failed to update critical status', err);
      }
    });
  }

  applyUserFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.userDataSource.filter = filterValue.trim().toLowerCase();

    if (this.userDataSource.paginator) {
      this.userDataSource.paginator.firstPage();
    }
  }

  applyPracticeFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.practiceDataSource.filter = filterValue.trim().toLowerCase();

    if (this.practiceDataSource.paginator) {
      this.practiceDataSource.paginator.firstPage();
    }
  }

  assiginUser(): void {
    if (!this.eligibleUsers.length) {
    }
    this.userrole();
    this.assignDialogSubmitted = false;
    this.selectedEligibleUser = null;
    this.selectedUserRole = null;
    this.selectedUserForAssignment = null;
    this.isAssignHospitalMode = false;
    this.showAssignUserDialog = true;
  }

  onAssignDialogCancel(): void {
    this.showAssignUserDialog = false;
    this.assignDialogSubmitted = false;
    this.selectedEligibleUser = null;
    this.selectedUserRole = null;
    this.selectedUserForAssignment = null;
    this.selectedHospital = null;
    this.isAssignHospitalMode = false;
  }

  onAssignDialogConfirm(): void {
    this.assignDialogSubmitted = true;

    if (!this.selectedEligibleUser || this.isAssigningUser) {
      return;
    }

    if (this.isAssignHospitalMode) {
      if (!this.selectedHospital) return;
      
      const payload = {
        userId: this.selectedEligibleUser.id,
        practiceSkey: this.selectedHospital.practiceSKey,
        isOverride: true
      };

      this.isAssigningUser = true;
      this.leaderService.upsertPracticeUser(payload).subscribe({
        next: () => {
          this.isAssigningUser = false;
          this.leaderService.clearHospitalListCache();
          this.leaderService.showNotification('Hospital assigned successfully', false);
          this.onAssignDialogCancel();
          this.userList();
        },
        error: (err) => {
          this.isAssigningUser = false;
          console.error('Failed to assign hospital', err);
          this.leaderService.showNotification('Failed to assign hospital', true);
        }
      });
    } else {
      if (this.selectedUserRole == null) return;

      const payload = this.buildManageAccessPayload(this.selectedEligibleUser, this.selectedUserRole);
      this.isAssigningUser = true;

      this.leaderService.manageUserAccess(payload).subscribe({
        next: () => {
          this.isAssigningUser = false;
          this.leaderService.showNotification('User assigned successfully', false);
          this.onAssignDialogCancel();
          this.userList();
        },
        error: (err) => {
          this.isAssigningUser = false;
          console.error('Failed to assign user', err);
          this.leaderService.showNotification('Failed to assign user', true);
        }
      });
    }
  }

  private buildManageAccessPayload(sourceUser: EligibleUserData, selectedUserRole: number): any {
    return {
      UserId : sourceUser.id,
      IsAccess: true,
      UserRole: selectedUserRole
    };
  }

  UnassignUserAccess(user: any): void {
    this.selectedUserForUnassign = user;
    this.showUnassignUserDialog = true;
  }

  onUnassignDialogCancel(): void {
    this.showUnassignUserDialog = false;
    this.selectedUserForUnassign = null;
  }

  onUnassignDialogConfirm(): void {
    if (!this.selectedUserForUnassign || this.isUnassigningUser) {
      return;
    }

    const payload = {
      UserId : this.selectedUserForUnassign.id,
      IsAccess: false
    };

    this.isUnassigningUser = true;
    this.leaderService.manageUserAccess(payload).subscribe({
      next: () => {
        this.isUnassigningUser = false;
        this.leaderService.showNotification('User access unassigned successfully', false);
        this.onUnassignDialogCancel();
        this.userList();
      },
      error: (err) => {
        this.isUnassigningUser = false;
        console.error('Failed to unassign user access', err);
        this.leaderService.showNotification('Failed to unassign user access', true);
      }
    });
  }

  editUser(user: UserData): void {}

  editHospitalAssignment(hospital: HospitalData): void {}

  getStatusClass(status: string): string {
    return status === 'Active' ? 'status-active' : 'status-inactive';
  }

  assignHospital(user?: any): void {
    this.assignDialogSubmitted = false;
    this.isAssignHospitalMode = true;
    this.selectedHospital = null;
    this.hospitalOptions = [];
    
    if (user) {
      this.selectedEligibleUser = this.mapEligibleUserData(user);
      // Ensure the selected user is in the options so the dropdown displays the name
      this.eligibleUserOptions = [{
        label: `${this.selectedEligibleUser.displayName}${this.selectedEligibleUser.email ? ` (${this.selectedEligibleUser.email})` : ''}`,
        value: this.selectedEligibleUser
      }];
      this.fetchHospitalsForUser(this.selectedEligibleUser.id);
    } else {
      this.selectedEligibleUser = null;
      this.eligibleUserOptions = [];
    }
    
    this.showAssignUserDialog = true;
  }

  onEligibleUserChange(): void {
    if (this.isAssignHospitalMode && this.selectedEligibleUser) {
      this.fetchHospitalsForUser(this.selectedEligibleUser.id);
    }
  }

  fetchHospitalsForUser(userId: string): void {
    this.isLoadingHospitals = true;
    this.leaderService.getHospitalsByUserOverride(userId).subscribe({
      next: (res: any) => {
        const hospitals = res?.data || [];
        this.userOverrides.set(userId as any, hospitals);
        this.hospitalOptions = hospitals.map((h: any) => ({
          label: h.practiceName,
          value: h
        }));
        this.isLoadingHospitals = false;
      },
      error: () => {
        this.isLoadingHospitals = false;
      }
    });
  }

  onHospitalSelect(event: any): void {
    if (event.value && event.value.isOverride) {
      this.leaderService.showNotification('This hospital is already assigned', true);
      setTimeout(() => {
        this.selectedHospital = null;
      });
    }
  }

  get assignedHospitals(): any[] {
    if (!this.selectedEligibleUser) return [];
    const overrides = this.userOverrides.get(this.selectedEligibleUser.id as any) || [];
    return overrides.filter((h: any) => h.isOverride);
  }

  onUnassignHospitalClick(hospital: any): void {
    this.selectedHospitalForUnassign = hospital;
    this.showUnassignHospitalDialog = true;
  }

  onUnassignHospitalCancel(): void {
    this.showUnassignHospitalDialog = false;
    this.selectedHospitalForUnassign = null;
  }

  onUnassignHospitalConfirm(): void {
    if (!this.selectedHospitalForUnassign || this.isUnassigningHospital || !this.selectedEligibleUser) {
      return;
    }

    const payload = {
      UserId: this.selectedEligibleUser.id,
      PracticeSkey: this.selectedHospitalForUnassign.practiceSKey
    };

    this.isUnassigningHospital = true;
    this.leaderService.deletePracticeUser(payload).subscribe({
      next: () => {
        this.isUnassigningHospital = false;
        this.leaderService.clearHospitalListCache();
        this.leaderService.showNotification('Hospital unassigned successfully', false);
        this.onUnassignHospitalCancel();
        this.fetchHospitalsForUser(this.selectedEligibleUser!.id);
        this.userList();
      },
      error: (err) => {
        this.isUnassigningHospital = false;
        console.error('Failed to unassign hospital', err);
        this.leaderService.showNotification('Failed to unassign hospital', true);
      }
    });
  }
}